import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, API } from "@/App";
import { toast } from "sonner";
import axios from "axios";
import { Mail, Lock, User, ArrowLeft, Ticket, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";

// Spardom Splash Logo Component - Large dominant hero element
const SpardomSplashLogo = () => (
  <div className="flex flex-col items-center">
    <img 
      src="/spardom-logo.png" 
      alt="Spardom" 
      className="h-56 sm:h-64 md:h-72 w-auto object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.15)]"
    />
  </div>
);

const RegisterPage = () => {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [validatingCode, setValidatingCode] = useState(false);
  const [codeValid, setCodeValid] = useState(null);
  const [codeRole, setCodeRole] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const validateInviteCode = useCallback(async (code) => {
    if (!code || code.length < 10) {
      setCodeValid(null);
      setCodeRole(null);
      return;
    }
    
    setValidatingCode(true);
    try {
      const res = await axios.get(`${API}/invite-codes/${code}/validate`);
      setCodeValid(true);
      setCodeRole(res.data.role);
    } catch (e) {
      setCodeValid(false);
      setCodeRole(null);
    } finally {
      setValidatingCode(false);
    }
  }, []);

  // Debounce invite code validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (inviteCode.length >= 10) {
        validateInviteCode(inviteCode);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [inviteCode, validateInviteCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!codeValid) {
      toast.error("Please enter a valid invite code");
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, { 
        email, 
        username, 
        password,
        invite_code: inviteCode
      });
      login(response.data.user, response.data.token);
      toast.success("Welcome to Spardom!");
      navigate("/browse");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    if (!codeValid) {
      toast.error("Please enter a valid invite code first");
      return;
    }
    // Store invite code for Google auth callback
    sessionStorage.setItem("pendingInviteCode", inviteCode);
    const redirectUrl = window.location.origin + '/browse';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden">
      {/* Neutral dark arena background with subtle ember glow */}
      <div className="absolute inset-0 bg-[#0B0F14]">
        {/* Subtle ember accent glow at top */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[40%] bg-gradient-to-b from-[#FF7A18]/10 via-[#FF7A18]/5 to-transparent blur-3xl" />
        {/* Subtle bottom glow */}
        <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-gradient-to-t from-[#182331]/50 to-transparent" />
      </div>
      
      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col px-6 pt-[max(env(safe-area-inset-top),12px)] pb-[max(env(safe-area-inset-bottom),16px)]">
        {/* Back button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#B7C3D0] hover:text-white group transition-colors text-sm w-fit font-medium"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back
        </Link>

        {/* Logo - Large dominant visual anchor */}
        <div className="flex justify-center mt-2 mb-6">
          <SpardomSplashLogo />
        </div>

        {/* Sign Up Section */}
        <div className="w-full max-w-sm mx-auto flex-1 flex flex-col">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 drop-shadow-lg">Join Spardom</h2>

          <form onSubmit={handleSubmit} className="space-y-2.5">
            {/* Invite Code - First and Required */}
            <div className="space-y-1">
              <Label htmlFor="inviteCode" className="text-[#B7C3D0] text-sm font-semibold flex items-center gap-2">
                <Ticket className="w-4 h-4 text-[#FF7A18]" />
                Invite Code <span className="text-[#FF7A18]">*</span>
              </Label>
              <div className="relative">
                <Input
                  id="inviteCode"
                  type="text"
                  data-testid="register-invite-code"
                  placeholder="SPAR-XXXX-XXXX"
                  value={inviteCode}
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    setInviteCode(value);
                    if (value.length < 10) {
                      setCodeValid(null);
                      setCodeRole(null);
                    }
                  }}
                  className={`h-11 bg-[#121A22] border-[#263445] text-white placeholder:text-[#8A96A6] focus:border-[#FF7A18] focus:ring-1 focus:ring-[#FF7A18]/30 rounded-full uppercase tracking-wider text-base font-medium shadow-lg ${
                    codeValid === true ? 'border-[#22C55E] focus:border-[#22C55E]' : codeValid === false ? 'border-[#EF4444] focus:border-[#EF4444]' : ''
                  }`}
                  required
                />
                {validatingCode && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-[#FF7A18]"></div>
                  </div>
                )}
                {!validatingCode && codeValid === true && (
                  <CheckCircle className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#22C55E]" />
                )}
                {!validatingCode && codeValid === false && (
                  <XCircle className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#EF4444]" />
                )}
              </div>
              {codeValid === true && codeRole && (
                <p className="text-xs text-[#22C55E] font-medium">
                  ✓ Valid code - You'll join as a <span className="font-bold capitalize">{codeRole}</span>
                </p>
              )}
              {codeValid === false && (
                <p className="text-xs text-[#EF4444] font-medium">Invalid or expired invite code</p>
              )}
            </div>

            {/* Username Field */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A96A6]">
                <User className="w-5 h-5" />
              </div>
              <Input
                id="username"
                type="text"
                data-testid="register-username"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="pl-12 h-11 bg-[#121A22] border-[#263445] text-white placeholder:text-[#8A96A6] focus:border-[#FF7A18] focus:ring-1 focus:ring-[#FF7A18]/30 rounded-full text-base font-medium shadow-lg disabled:opacity-50"
                required
                disabled={!codeValid}
              />
            </div>

            {/* Email Field */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A96A6]">
                <Mail className="w-5 h-5" />
              </div>
              <Input
                id="email"
                type="email"
                data-testid="register-email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-12 h-11 bg-[#121A22] border-[#263445] text-white placeholder:text-[#8A96A6] focus:border-[#FF7A18] focus:ring-1 focus:ring-[#FF7A18]/30 rounded-full text-base font-medium shadow-lg disabled:opacity-50"
                required
                disabled={!codeValid}
              />
            </div>

            {/* Password Field */}
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A96A6]">
                <Lock className="w-5 h-5" />
              </div>
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                data-testid="register-password"
                placeholder="••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-12 pr-12 h-11 bg-[#121A22] border-[#263445] text-white placeholder:text-[#8A96A6] focus:border-[#FF7A18] focus:ring-1 focus:ring-[#FF7A18]/30 rounded-full text-base font-medium shadow-lg disabled:opacity-50"
                minLength={6}
                required
                disabled={!codeValid}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#8A96A6] hover:text-white transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {/* Create Account Button - Ember accent */}
            <Button
              type="submit"
              data-testid="register-submit"
              disabled={loading || !codeValid}
              className="w-full bg-[#FF7A18] hover:bg-[#FF9A4A] text-white h-11 font-bold uppercase tracking-widest rounded-full transition-all shadow-xl shadow-[#FF7A18]/20 disabled:opacity-50 text-base mt-1"
            >
              {loading ? "Creating..." : "Create Account"}
            </Button>
          </form>

          {/* Social Login Section */}
          <div className="mt-5">
            <p className="text-center text-[#B7C3D0] mb-3 text-sm font-medium">Or Sign Up With</p>
            
            <div className="flex justify-center">
              {/* Google Button */}
              <button
                type="button"
                data-testid="google-register"
                onClick={handleGoogleLogin}
                disabled={!codeValid}
                className="flex items-center gap-3 text-white hover:text-white/90 transition-colors bg-[#182331] hover:bg-[#263445] border border-[#263445] px-8 py-2.5 rounded-full disabled:opacity-50"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <svg className="w-4 h-4" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <span className="text-sm uppercase tracking-wide font-semibold">Google</span>
              </button>
            </div>
          </div>

          {/* Sign In Link */}
          <p className="text-center text-[#B7C3D0] mt-auto pt-3 text-sm font-medium">
            Already have an account?{" "}
            <Link to="/login" className="text-[#FF7A18] hover:text-[#FF9A4A] hover:underline font-bold transition-colors">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
